import { useMemo } from 'react';
import { CustomError } from '../../errors';
import {
    TransactionEntryCategoryFieldsFragment,
    TransactionEntryFieldsFragment,
} from '../../graphql/generated';
import { getErrorLogger } from '../errorLogger';
import { CashflowFinancialEntity, CashflowTransactionEntry } from './types';

export const useTransactionEntries = ({
    transactionEntries,
    financialEntityMap,
    transactionEntryCategoriesMapById,
}: {
    transactionEntries?: TransactionEntryFieldsFragment[] | null;
    financialEntityMap?: Record<string, CashflowFinancialEntity>;
    transactionEntryCategoriesMapById?: Record<
        string,
        TransactionEntryCategoryFieldsFragment
    >;
}) => {
    return useMemo(() => {
        if (
            !transactionEntries ||
            !financialEntityMap ||
            !transactionEntryCategoriesMapById
        ) {
            return [];
        }

        return buildTransactionEntries(
            transactionEntries,
            financialEntityMap,
            transactionEntryCategoriesMapById
        );
    }, [
        transactionEntries,
        financialEntityMap,
        transactionEntryCategoriesMapById,
    ]);
};

// Add financialEntity data from currentFinances to each transaction entry
// Add category data from categories query to each transaction entry
// Avoids extra data in the large transactionEntries query
const buildTransactionEntries = (
    transactionEntries: TransactionEntryFieldsFragment[],
    financialEntityMap: Record<string, CashflowFinancialEntity>,
    transactionEntryCategoriesMap: Record<
        string,
        TransactionEntryCategoryFieldsFragment | undefined
    >
) => {
    const logError = getErrorLogger();
    const results: CashflowTransactionEntry[] = [];

    for (const t of transactionEntries) {
        // Handle the case where a transaction has been deleted but new data hasn't been fetched
        if (t.deletedAt) continue;

        const financialEntity = financialEntityMap[t.financialEntityId];
        if (!financialEntity) {
            continue;
        }

        const category = transactionEntryCategoriesMap[t.categoryId];

        if (!category) {
            logError(
                new CustomError('Missing category for transaction entry'),
                { transactionEntry: t }
            );
            continue;
        }

        results.push({
            ...t,
            financialEntity,
            category,
        });
    }

    return results;
};
